/**
 * Save trees drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawing_topics`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const saveTreesDrawingsPack: ToilePack = {
    slug: 'save-trees-drawings',
    category: 'coloring',
    name: 'Save trees drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'crying', label: 'Crying tree', subject: 'a crying tree with an axe stuck in its trunk and stumps around it'},
        {key: 'lungs', label: 'Tree lungs', subject: 'a tree whose branches form a pair of lungs, birds sitting in it'},
        {key: 'hug', label: 'Tree hug', subject: 'a child hugging a large tree trunk, roots spreading and leaves falling'},
    ],
    styles: [
        {key: 'pencil', label: 'Pencil sketch', style: 'a soft graphite pencil sketch with clear outlines and light hatching for shade', palette: 'Gray pencil on white paper, no color.', framing: 'Drawn on a plain white paper sheet, centered with a white margin.'},
        {key: 'crayon', label: 'Colour pencil', style: 'a colour pencil and crayon drawing, simple outlines filled with bright even strokes of colour', palette: 'Bright crayon colours on white paper.', framing: 'Drawn on a plain white drawing-book page, centered with a white margin.'},
        {key: 'outline', label: 'Outline to colour', style: 'a bold black outline drawing with flat marker colour filling each shape', palette: 'Black outlines with flat bright colour fills on white paper.', framing: 'Flat 2D drawing isolated on a pure white background with a white margin, clean closed black line work.'},
    ],
    directions: ['an axe', 'a bird nest', 'a forest'],
    directionHint: 'try: an axe',
    seo: {
        title: 'Save Trees Drawings: Crying Tree, Tree Lungs, Tree Hug',
        metaDescription: 'Nine save trees drawings: a crying tree, a tree shaped as lungs and a child hugging a tree, in pencil, colour and outline.',
        h1: 'Save trees drawings: crying tree, tree as lungs, child hugging a tree',
        tagline: 'Three ideas, each in pencil sketch, colour pencil and outline to colour.',
        intro: 'Save trees drawings for a school competition, a chart or a tree planting project. The set shows a crying tree with an axe in its trunk and stumps around it, a tree whose branches form a pair of lungs with birds in it, and a child hugging a large tree trunk. Each comes in three styles: pencil, colour pencil and outline to colour. Type a change in the direction field, such as a bird nest or a forest.',
        useCases: [
            'a save trees poster for a competition',
            'a tree planting project chart',
            'an easy colouring sheet',
            'a classroom display on the environment',
        ],
        faq: [
            {q: 'Can I print these for a school competition?', a: 'Yes. Each picture is a plain drawing on a white background, so it prints on ordinary paper.'},
            {q: 'What is the difference between the three styles?', a: 'Pencil sketch is grey shading with no colour. Colour pencil is bright crayon colour. Outline to colour is a bold black line drawing with flat colour inside each shape, easy to fill in.'},
            {q: 'What does the tree as lungs show?', a: 'It shows that trees give us clean air, drawn as a tree whose branches make the shape of lungs.'},
            {q: 'Can I ask for a different picture?', a: 'Yes. Type the change in the direction field. It is a request, so the result may differ from the nine shown.'},
        ],
    },
}
